import { Box, Table } from '@factorialco/deprecated-design-system'
import {
  EmployeeCustomTablesResourceValueFragment,
  EmployeeCustomTablesSchemaFragment,
  EmployeeCustomTablesSchemaResourceFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import useSearch from 'modules/core/hooks/useSearch'
import { matchText } from 'modules/core/lib/stringUtils'

import { coerceValueFromType } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/coerceValueFromType'
import Options from 'modules/employees/components/CustomTables/List/_List/_CustomTable/_Options'
import TableBody from 'modules/employees/components/CustomTables/List/_List/_CustomTable/_TableBody'
import TableHeader from 'modules/employees/components/CustomTables/List/_List/_CustomTable/_TableHeader'

const useFilteredValues = createComputed(
  (
    search: string,
    resourceValues: EmployeeCustomTablesResourceValueFragment[]
  ): EmployeeCustomTablesResourceValueFragment[] => {
    if (!resourceValues || isEmpty(search)) return resourceValues

    return resourceValues?.filter((resourceValue) => {
      const customValues = resourceValue.customValues ?? []
      if (customValues.length === 0) return false

      return customValues.some((customValue) => {
        if (!customValue.fieldType) return false

        const rawValue = coerceValueFromType(customValue, customValue.fieldType)
        if (!rawValue) return false

        return matchText(search, String(rawValue))
      })
    })
  }
)
interface Props {
  canManage: boolean
  canAddValues: boolean
  attachableId: string
  schema: EmployeeCustomTablesSchemaFragment
  currency: string
}

const TableComponent = ({ attachableId, schema, canManage, canAddValues, currency }: Props) => {
  const { search, onSearch } = useSearch()
  const resources: EmployeeCustomTablesSchemaResourceFragment[] =
    schema.resourcesConnection?.nodes ?? []

  const resourceValues: EmployeeCustomTablesResourceValueFragment[] = resources.flatMap(
    (resource) => resource.resourceValues
  )
  const filteredValues = useFilteredValues(search, resourceValues).get()

  const TableOptions = () => (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s24" flexGrow>
      <Options
        schemaId={schema.id.toString()}
        search={search}
        onSearch={onSearch}
        canAddValues={canAddValues}
        canManage={canManage}
      />
    </Box>
  )

  const sortedFields = sortBy(schema.fields, 'position')
  const fieldLabels: string[] = sortedFields.map((f) => f.field?.labelText).filter(Boolean)

  return (
    <Box overflowX="scroll">
      <Table options={TableOptions} style={{ minWidth: '100%', width: 'min-content' }} autoLayout>
        <TableHeader fieldsLabels={fieldLabels} />
        <TableBody
          attachableId={attachableId}
          schema={schema}
          currency={currency}
          filteredResourceValues={filteredValues}
          sortedFields={sortedFields}
        />
      </Table>
    </Box>
  )
}

export default observer(TableComponent)
